<template>
  <ion-page>
    <PageHeaderWithMenu title="Тестирование" :scrolled="headerScrolled" />
    <ion-content
      :class="[isMobile ? 'main-background' : 'liga-web-background']"
      :fullscreen="true"
      @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="loadTests($event)">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <ion-card v-show="showCreateForm" class="admin-card">
        <ion-card-header>
          <ion-card-title>Создать тест</ion-card-title>
          <ion-buttons slot="end">
            <ion-button fill="clear" size="small" class="admin-card-btn" @click="showCreateForm = false">Свернуть</ion-button>
          </ion-buttons>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-input label="Название" label-placement="stacked" v-model="newTest.name" placeholder="Название теста"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Описание (необязательно)" label-placement="stacked" v-model="newTest.description" placeholder="Описание"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Порог успешности (%)" label-placement="stacked" type="number" v-model.number="newTest.passingThreshold" min="0" max="100"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Макс. попыток (пусто = без ограничения)" label-placement="stacked" type="number" v-model.number="newTest.maxAttempts" placeholder="Пусто"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Время на прохождение (ЧЧ:ММ, пусто = без ограничения)" label-placement="stacked" :model-value="(newTest.durationForm as unknown as number)" @update:model-value="(v: number) => onNewTestDurationInput(v)" placeholder="00:00" :maxlength="5"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Открытие прохождения (пусто = сразу)</ion-label>
            <div class="admin-available-from-row">
              <span class="admin-available-from-value">
                {{ newTest.availableFromInput ? formatAvailableFromLocal(newTest.availableFromInput) : 'Сразу после назначения' }}
              </span>
              <ion-button fill="clear" size="small" class="admin-card-btn" @click="openNewAvailableFrom = true">
                {{ newTest.availableFromInput ? 'Изменить' : 'Указать' }}
              </ion-button>
              <ion-button
                v-if="newTest.availableFromInput"
                fill="clear"
                size="small"
                class="admin-card-btn"
                @click="clearNewAvailableFrom"
              >Очистить</ion-button>
            </div>
          </ion-item>
          <ion-modal class="datetime-modal" :is-open="openNewAvailableFrom" @didDismiss="openNewAvailableFrom = false">
            <ion-datetime
              presentation="date-time"
              locale="ru-RU"
              :prefer-wheel="false"
              :value="newTest.availableFromInput || todayIso"
              show-default-buttons
              done-text="Готово"
              cancel-text="Отмена"
              @ionChange="onNewAvailableFromChange($event)"
              @ionCancel="openNewAvailableFrom = false"
            ></ion-datetime>
          </ion-modal>
          <ion-item>
            <ion-select label="Роли при успешном прохождении" label-placement="stacked" v-model="newTest.roleNames" multiple interface="popover" mode="ios" placeholder="Выберите роли">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-button expand="block" class="admin-primary-btn" @click="createTest()" :disabled="!newTest.name?.trim() || creating">
            {{ creating ? 'Создание…' : 'Создать тест' }}
          </ion-button>
          <ion-button expand="block" fill="outline" size="small" class="admin-outline-btn" @click="showCreateForm = false">Отмена</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Список тестов</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-button v-if="!showCreateForm" expand="block" class="admin-primary-btn" @click="showCreateForm = true">+ Создать тест</ion-button>
          <p v-if="loading" class="admin-hint">Загрузка…</p>
          <p v-else-if="!tests.length" class="admin-hint">Тестов пока нет.</p>
          <div v-else class="admin-tests-list">
            <div v-for="t in tests" :key="t.id" class="admin-test-row">
              <div class="admin-test-main">
                <ion-label>
                  <h2>{{ t.name }}</h2>
                  <p>Порог: {{ t.passingThreshold }}% · Попыток: {{ t.maxAttempts ?? '∞' }} · Время: {{ formatDurationDisplay(t.durationMinutes) }}</p>
                  <p v-if="t.availableFrom">Открытие: {{ formatAvailableFromDisplay(t.availableFrom) }}</p>
                </ion-label>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="openEditModal(t)">
                  <ion-icon :icon="pencilOutline"></ion-icon>
                  Редактировать
                </ion-button>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="openQuestionsModal(t)">
                  Вопросы
                </ion-button>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="toggleAssignments(t)">
                  <ion-icon :icon="peopleOutline"></ion-icon>
                  Назначения
                </ion-button>
                <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteTestConfirm(t)">
                  <ion-icon :icon="trashOutline"></ion-icon>
                </ion-button>
              </div>
              <div v-show="expandedTestId === t.id" class="admin-test-assignments">
                <p class="admin-assignments-title">Кому назначен тест</p>
                <ion-searchbar v-if="currentAssignments.length" v-model="assignmentsFilterQuery" placeholder="Поиск по имени или логину" :debounce="200" class="admin-assignments-search"></ion-searchbar>
                <div v-if="assignmentsLoading" key="assign-loading" class="admin-hint">Загрузка…</div>
                <ion-list v-else-if="filteredAssignments.length" key="assign-list" lines="full" class="admin-assignments-list">
                  <ion-item v-for="a in filteredAssignments" :key="'a-' + a.id + '-' + a.userId" class="admin-assignment-item">
                    <ion-label>
                      <h3>{{ assignmentDisplayName(a) }}</h3>
                      <p class="admin-assignment-login">{{ a.login }}</p>
                      <div class="admin-assignment-status-row">
                        <span v-if="a.passed" class="admin-assignment-status admin-assignment-status--passed">
                          <ion-icon :icon="checkmarkCircle"></ion-icon> Пройден
                        </span>
                        <span v-else-if="a.hasDraft" class="admin-assignment-status admin-assignment-status--draft">
                          <ion-icon :icon="createOutline"></ion-icon> В процессе
                        </span>
                        <span v-else-if="a.usedAttempts > 0" class="admin-assignment-status admin-assignment-status--failed">
                          Не пройден
                        </span>
                        <span v-else class="admin-assignment-status admin-assignment-status--none">
                          Не начат
                        </span>
                        <span class="admin-assignment-attempts">Попыток: {{ a.usedAttempts }}</span>
                        <span v-if="a.lastScore != null" class="admin-assignment-score">Результат: {{ a.lastScore }}%</span>
                      </div>
                      <div class="admin-assignment-actions">
                        <ion-button v-if="a.usedAttempts > 0" fill="clear" size="small" class="admin-card-btn" @click="openAttemptDetails(t.id, a.userId)">Детализация</ion-button>
                        <ion-button fill="clear" size="small" class="admin-card-btn" @click="resetAttempts(t.id, a.userId)">Сбросить попытки</ion-button>
                        <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="removeAssignment(t.id, a.userId)">
                          <ion-icon :icon="trashOutline"></ion-icon>
                        </ion-button>
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-list>
                <p v-else-if="currentAssignments.length && !filteredAssignments.length" key="assign-filter-empty" class="admin-hint">Ничего не найдено.</p>
                <p v-else key="assign-none" class="admin-hint">Никому не назначен.</p>
                <ion-button expand="block" fill="outline" size="small" class="admin-outline-btn" @click="openAddUsersModal(t)">Добавить пользователей</ion-button>
              </div>
            </div>
          </div>
        </ion-card-content>
      </ion-card>
    </ion-content>

    <!-- Модальное окно выбора пользователей -->
    <ion-modal :is-open="showAddUsersModal" @didDismiss="closeAddUsersModal" class="admin-users-modal">
      <div class="admin-users-sheet">
        <div class="admin-users-head">
          <h2>Добавить пользователей</h2>
          <button type="button" class="admin-users-close" aria-label="Закрыть" @click="closeAddUsersModal">
            Закрыть
          </button>
        </div>

        <p v-if="addUsersModalLoading" class="admin-users-status">Загрузка списка пользователей…</p>
        <p v-else-if="!availableUsers.length" class="admin-users-status">
          Нет пользователей для добавления (все уже назначены).
        </p>
        <template v-else>
          <ion-searchbar
            v-model="addUsersFilterQuery"
            placeholder="Поиск по имени, фамилии или логину"
            :debounce="200"
            class="admin-add-users-search"
          />
          <div class="admin-role-filter" role="toolbar" aria-label="Фильтр по роли">
            <button
              type="button"
              class="admin-role-chip"
              :class="{ 'admin-role-chip--active': !addUsersRoleFilter }"
              @click="addUsersRoleFilter = ''"
            >
              Все
            </button>
            <button
              v-for="role in addUsersRoleOptions"
              :key="role.name"
              type="button"
              class="admin-role-chip"
              :class="{ 'admin-role-chip--active': addUsersRoleFilter === role.name }"
              @click="addUsersRoleFilter = role.name"
            >
              {{ role.description }}
            </button>
          </div>
          <div class="admin-users-list-wrap">
            <ion-list v-if="filteredAvailableUsers.length" lines="none" class="admin-users-pick-list">
              <ion-item
                v-for="u in filteredAvailableUsers"
                :key="u.id"
                button
                detail="false"
                class="admin-user-pick-item"
                @click="toggleUserPick(u.id)"
              >
                <ion-checkbox
                  slot="start"
                  :checked="selectedUserIds.includes(u.id)"
                  @click.prevent.stop="toggleUserPick(u.id)"
                />
                <ion-label>
                  <h3>{{ [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.login }}</h3>
                  <p class="admin-user-pick-login">{{ u.login }}</p>
                </ion-label>
              </ion-item>
            </ion-list>
            <p v-else class="admin-users-status">Ничего не найдено.</p>
          </div>
          <div class="admin-users-actions">
            <ion-button
              expand="block"
              class="admin-primary-btn"
              :disabled="!selectedUserIds.length || addingUsers"
              @click="confirmAddUsers"
            >
              {{ addingUsers ? 'Добавление…' : 'Добавить выбранных' }}
            </ion-button>
            <ion-button expand="block" fill="outline" class="admin-outline-btn" @click="closeAddUsersModal">
              Отмена
            </ion-button>
          </div>
        </template>
      </div>
    </ion-modal>

    <!-- Модальное окно детализации последней попытки -->
    <ion-modal :is-open="showAttemptDetailsModal" @didDismiss="closeAttemptDetailsModal" class="admin-attempt-details-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Детализация попытки</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeAttemptDetailsModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <p v-if="attemptDetailsLoading" class="admin-hint">Загрузка…</p>
        <template v-else-if="attemptDetails">
          <div class="admin-attempt-summary">
            <p>Пользователь: {{ attemptDetails.login }}</p>
            <p>Результат: {{ attemptDetails.score ?? 0 }}% · {{ attemptDetails.passed ? 'Пройден' : 'Не пройден' }}</p>
          </div>
          <div class="admin-attempt-question-list">
            <div
              v-for="q in attemptDetails.questionResults"
              :key="q.questionId"
              :class="['admin-attempt-question', q.correct ? 'admin-attempt-question--correct' : 'admin-attempt-question--wrong']">
              <div class="admin-attempt-question-header">
                <strong>{{ q.questionText }}</strong>
                <span>{{ q.correct ? 'Верно' : 'Неверно' }}</span>
              </div>
              <p class="admin-attempt-answer-line">
                Ответ пользователя: {{ formatAttemptAnswers(q.selectedAnswers) }}
              </p>
              <p class="admin-attempt-answer-line">
                Правильный ответ: {{ formatAttemptAnswers(q.correctAnswers) }}
              </p>
            </div>
          </div>
        </template>
        <p v-else class="admin-hint">Детализация не найдена.</p>
      </ion-content>
    </ion-modal>

    <!-- Модальное окно редактирования теста -->
    <ion-modal :is-open="showEditModal" @didDismiss="closeEditModal" class="admin-edit-test-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Редактировать тест</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeEditModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <p v-if="editLoading" class="admin-hint">Загрузка…</p>
        <template v-else>
          <ion-item>
            <ion-input label="Название" label-placement="stacked" v-model="editTest.name" placeholder="Название теста"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Описание (необязательно)" label-placement="stacked" v-model="editTest.description" placeholder="Описание"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Порог успешности (%)" label-placement="stacked" type="number" v-model.number="editTest.passingThreshold" min="0" max="100"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Макс. попыток (пусто = без ограничения)" label-placement="stacked" type="number" v-model.number="editTest.maxAttempts" placeholder="Пусто"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Время на прохождение (ЧЧ:ММ, пусто = без ограничения)" label-placement="stacked" :model-value="(editTest.durationForm as unknown as number)" @update:model-value="(v: number) => onEditTestDurationInput(v)" placeholder="00:00" :maxlength="5"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Открытие прохождения (пусто = сразу)</ion-label>
            <div class="admin-available-from-row">
              <span class="admin-available-from-value">
                {{ editTest.availableFromInput ? formatAvailableFromLocal(editTest.availableFromInput) : 'Сразу после назначения' }}
              </span>
              <ion-button fill="clear" size="small" class="admin-card-btn" @click="openEditAvailableFrom = true">
                {{ editTest.availableFromInput ? 'Изменить' : 'Указать' }}
              </ion-button>
              <ion-button
                v-if="editTest.availableFromInput"
                fill="clear"
                size="small"
                class="admin-card-btn"
                @click="clearEditAvailableFrom"
              >Очистить</ion-button>
            </div>
          </ion-item>
          <ion-modal class="datetime-modal" :is-open="openEditAvailableFrom" @didDismiss="openEditAvailableFrom = false">
            <ion-datetime
              presentation="date-time"
              locale="ru-RU"
              :prefer-wheel="false"
              :value="editTest.availableFromInput || todayIso"
              show-default-buttons
              done-text="Готово"
              cancel-text="Отмена"
              @ionChange="onEditAvailableFromChange($event)"
              @ionCancel="openEditAvailableFrom = false"
            ></ion-datetime>
          </ion-modal>
          <ion-item>
            <ion-select :key="'edit-roles-' + editTestId + '-' + (editTest.roleNames || []).join(',')" label="Роли при успешном прохождении" label-placement="stacked" v-model="editTest.roleNames" multiple interface="popover" mode="ios" placeholder="Выберите роли">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <div class="admin-modal-actions">
            <ion-button expand="block" class="admin-primary-btn" @click="saveEditTest()" :disabled="!editTest.name?.trim() || saving">
              {{ saving ? 'Сохранение…' : 'Сохранить' }}
            </ion-button>
            <ion-button expand="block" fill="outline" class="admin-outline-btn" @click="closeEditModal">Отмена</ion-button>
          </div>
        </template>
      </ion-content>
    </ion-modal>

    <!-- Модальное окно вопросов и ответов теста -->
    <ion-modal :is-open="showQuestionsModal" @didDismiss="closeQuestionsModal" class="admin-questions-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Вопросы и ответы{{ questionsTestName ? ': ' + questionsTestName : '' }}</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeQuestionsModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding admin-questions-content">
        <p v-if="questionsLoading" class="admin-hint">Загрузка…</p>
        <template v-else>
          <div v-if="showAddQuestionForm" class="admin-question-form admin-card">
            <p class="admin-assignments-title">Новый вопрос</p>
            <ion-item>
              <ion-input label="Текст вопроса" label-placement="stacked" v-model="newQuestion.text" placeholder="Введите вопрос"></ion-input>
            </ion-item>
            <ion-item>
              <ion-checkbox labelPlacement="end" v-model="newQuestion.multipleChoice">Несколько правильных ответов</ion-checkbox>
            </ion-item>
            <ion-item>
              <ion-input label="Порядок (число)" label-placement="stacked" type="number" v-model.number="newQuestion.sortOrder" placeholder="0"></ion-input>
            </ion-item>
            <div class="admin-modal-actions">
              <ion-button size="small" class="admin-primary-btn" @click="saveNewQuestion()" :disabled="!newQuestion.text?.trim() || saving">Сохранить вопрос</ion-button>
              <ion-button size="small" fill="outline" class="admin-outline-btn" @click="cancelAddQuestion()">Отмена</ion-button>
            </div>
          </div>
          <template v-else>
            <ion-button expand="block" size="small" class="admin-primary-btn" @click="showAddQuestionForm = true">+ Добавить вопрос</ion-button>
          </template>

          <div v-if="questionsList.length" class="admin-questions-list">
            <div v-for="q in questionsList" :key="q.id" class="admin-question-block">
              <div class="admin-question-header">
                <div class="admin-question-title">
                  <strong>{{ q.text || '(без текста)' }}</strong>
                  <span v-if="q.multipleChoice" class="admin-badge">несколько</span>
                  <span class="admin-question-order">порядок: {{ q.sortOrder ?? 0 }}</span>
                </div>
                <div class="admin-question-actions">
                  <ion-button fill="clear" size="small" class="admin-card-btn" @click="startEditQuestion(q)">Изменить</ion-button>
                  <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteQuestionConfirm(q)">Удалить</ion-button>
                </div>
              </div>
              <div v-if="editQuestionId === q.id" class="admin-question-form admin-edit-inline">
                <ion-item>
                  <ion-input label="Текст вопроса" label-placement="stacked" v-model="editQuestionForm.text" placeholder="Введите вопрос"></ion-input>
                </ion-item>
                <ion-item>
                  <ion-checkbox labelPlacement="end" v-model="editQuestionForm.multipleChoice">Несколько правильных ответов</ion-checkbox>
                </ion-item>
                <ion-item>
                  <ion-input label="Порядок" label-placement="stacked" type="number" v-model.number="editQuestionForm.sortOrder"></ion-input>
                </ion-item>
                <ion-button size="small" class="admin-primary-btn" @click="saveEditQuestion()" :disabled="saving">Сохранить</ion-button>
                <ion-button size="small" fill="outline" class="admin-outline-btn" @click="editQuestionId = null">Отмена</ion-button>
              </div>
              <ul class="admin-answers-list">
                <li v-for="ans in (q.answers || [])" :key="ans.id" class="admin-answer-row">
                  <span class="admin-answer-text">{{ ans.text }}</span>
                  <span v-if="ans.isCorrect" class="admin-badge correct">верный</span>
                  <template v-if="editAnswerId === ans.id">
                    <ion-input v-model="editAnswerForm.text" placeholder="Текст ответа" class="admin-answer-input"></ion-input>
                    <ion-checkbox v-model="editAnswerForm.isCorrect" labelPlacement="end" class="admin-answer-checkbox">Верный</ion-checkbox>
                    <ion-button size="small" class="admin-primary-btn" @click="saveEditAnswer()" :disabled="saving">Сохранить</ion-button>
                    <ion-button size="small" fill="outline" class="admin-outline-btn" @click="editAnswerId = null">Отмена</ion-button>
                  </template>
                  <template v-else>
                    <ion-button fill="clear" size="small" class="admin-card-btn" @click="startEditAnswer(ans)">Изменить</ion-button>
                    <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteAnswerConfirm(ans)">Удалить</ion-button>
                  </template>
                </li>
                <li v-if="addAnswerForQid === q.id" class="admin-answer-row admin-add-answer">
                  <ion-input v-model="newAnswer.text" placeholder="Текст ответа" class="admin-answer-input"></ion-input>
                  <ion-checkbox v-model="newAnswer.isCorrect" labelPlacement="end">Верный</ion-checkbox>
                  <ion-button size="small" class="admin-primary-btn" @click="saveNewAnswer(q.id)" :disabled="!newAnswer.text?.trim() || saving">Добавить</ion-button>
                  <ion-button size="small" fill="outline" class="admin-outline-btn" @click="cancelAddAnswer()">Отмена</ion-button>
                </li>
              </ul>
              <ion-button v-if="addAnswerForQid !== q.id" size="small" fill="outline" class="admin-outline-btn admin-add-answer-btn" @click="startAddAnswer(q)">+ Добавить ответ</ion-button>
            </div>
          </div>
          <p v-else-if="!showAddQuestionForm" class="admin-hint">Вопросов пока нет. Добавьте первый вопрос.</p>
        </template>
      </ion-content>
    </ion-modal>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { GlobalContext } from '@/model/globalcontext.model';
import type {
  IAdminAnswer,
  IAdminQuestion,
  IAdminTest,
  IUserTestAttemptDetailsVM,
  IUserTestAssignmentVM,
} from '@/services/admin.service';
import AdminService from '@/services/admin.service';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonDatetime,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import { checkmarkCircle, closeOutline, createOutline, peopleOutline, pencilOutline, trashOutline } from 'ionicons/icons';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { defineComponent, inject } from 'vue';
import dayjs from 'dayjs';

interface TestFormVM {
  name: string;
  description: string;
  passingThreshold: number;
  maxAttempts: number | undefined;
  durationForm: string;
  /** Локальное значение для ion-datetime: YYYY-MM-DDTHH:mm */
  availableFromInput: string;
  roleNames: string[];
}

export default defineComponent({
  name: 'AdminTests',
  components: {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCheckbox,
    IonContent,
    IonDatetime,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    function assignmentDisplayName(a: IUserTestAssignmentVM): string {
      const parts = [a.firstName, a.lastName].filter(Boolean);
      return parts.length ? parts.join(' ').trim() : a.login;
    }
    return { isMobile: globalContext.isMobile, checkmarkCircle, closeOutline, createOutline, peopleOutline, pencilOutline, trashOutline, assignmentDisplayName };
  },
  data() {
    return {
      adminService: new AdminService(),
      headerScrolled: false,
      showCreateForm: false,
      loading: false,
      tests: [] as IAdminTest[],
      newTest: {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        availableFromInput: '',
        roleNames: [] as string[],
      } as TestFormVM,
      openNewAvailableFrom: false,
      creating: false,
      allAuthorities: [] as Authority[],
      expandedTestId: null as number | null,
      currentAssignments: [] as IUserTestAssignmentVM[],
      assignmentsLoading: false,
      showAddUsersModal: false,
      addUsersModalLoading: false,
      addUsersTestId: null as number | null,
      availableUsers: [] as { id: number; login: string; firstName?: string; lastName?: string; authorities?: string[] }[],
      selectedUserIds: [] as number[],
      addingUsers: false,
      showEditModal: false,
      editTestId: null as number | null,
      editTest: {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        availableFromInput: '',
        roleNames: [] as string[],
      } as TestFormVM,
      openEditAvailableFrom: false,
      editLoading: false,
      saving: false,
      showQuestionsModal: false,
      questionsTestId: null as number | null,
      questionsTestName: '' as string,
      questionsList: [] as IAdminQuestion[],
      questionsLoading: false,
      showAddQuestionForm: false,
      newQuestion: { text: '', multipleChoice: false, sortOrder: 0 },
      addAnswerForQid: null as number | null,
      newAnswer: { text: '', isCorrect: false },
      editQuestionId: null as number | null,
      editQuestionForm: { text: '', multipleChoice: false, sortOrder: 0 },
      editAnswerId: null as number | null,
      editAnswerForm: { text: '', isCorrect: false },
      assignmentsFilterQuery: '',
      addUsersFilterQuery: '',
      addUsersRoleFilter: '',
      showAttemptDetailsModal: false,
      attemptDetailsLoading: false,
      attemptDetails: null as IUserTestAttemptDetailsVM | null,
    };
  },
  computed: {
    todayIso(): string {
      return dayjs().format('YYYY-MM-DDTHH:mm');
    },
    filteredAvailableUsers(): { id: number; login: string; firstName?: string; lastName?: string; authorities?: string[] }[] {
      const q = (this.addUsersFilterQuery || '').trim().toLowerCase();
      const role = this.addUsersRoleFilter;
      return this.availableUsers.filter((u) => {
        if (role && !(u.authorities || []).includes(role)) return false;
        if (!q) return true;
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim().toLowerCase();
        const login = (u.login || '').toLowerCase();
        return name.includes(q) || login.includes(q);
      });
    },
    addUsersRoleOptions(): Array<{ name: string; description: string }> {
      return (this.allAuthorities || [])
        .filter((a) => a?.name)
        .map((a) => ({
          name: a.name,
          description: (a.description || '').trim() || a.name.replace(/^ROLE_/, ''),
        }))
        .sort((a, b) => a.description.localeCompare(b.description, 'ru'));
    },
    filteredAssignments(): IUserTestAssignmentVM[] {
      const q = (this.assignmentsFilterQuery || '').trim().toLowerCase();
      if (!q) return this.currentAssignments;
      return this.currentAssignments.filter((a) => {
        const name = ([a.firstName, a.lastName].filter(Boolean).join(' ').trim() || a.login).toLowerCase();
        const login = (a.login || '').toLowerCase();
        return name.includes(q) || login.includes(q);
      });
    },
  },
  async ionViewWillEnter() {
    this.allAuthorities = await this.adminService.getAuthoritiesFull();
    await this.loadTests();
  },
  methods: {
    onContentScroll(e: Event) {
      const ev = e as CustomEvent;
      this.headerScrolled = (ev.detail?.scrollTop ?? 0) > 30;
    },
    async loadTests(event?: CustomEvent) {
      this.loading = true;
      const expandedId = this.expandedTestId;
      try {
        this.tests = await this.adminService.getAdminTests();
        if (expandedId != null) {
          this.currentAssignments = await this.adminService.getTestAssignments(expandedId);
        }
      } catch (e) {
        console.error('loadTests', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки тестов',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.loading = false;
        (event as CustomEvent & { target?: { complete?: () => void } })?.target?.complete?.();
      }
    },
    async createTest() {
      const name = this.newTest.name?.trim();
      if (!name) return;
      this.creating = true;
      try {
        await this.adminService.createTest({
          name,
          description: this.newTest.description?.trim() || undefined,
          passingThreshold: this.newTest.passingThreshold ?? 80,
          maxAttempts: this.newTest.maxAttempts ?? undefined,
          durationMinutes: this.parseDurationForm(this.newTest.durationForm),
          availableFrom: this.availableFromInputToIso(this.newTest.availableFromInput),
          roleNames: this.newTest.roleNames ?? [],
        });
        this.newTest.name = '';
        this.newTest.description = '';
        this.newTest.passingThreshold = 80;
        this.newTest.maxAttempts = undefined;
        this.newTest.durationForm = '';
        this.newTest.availableFromInput = '';
        this.newTest.roleNames = [];
        this.showCreateForm = false;
        const toast = await toastController.create({
          message: 'Тест создан',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        await this.loadTests();
      } catch (e) {
        console.error('createTest', e);
        const toast = await toastController.create({
          message: 'Ошибка создания теста',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creating = false;
      }
    },
    async toggleAssignments(t: IAdminTest) {
      if (this.expandedTestId === t.id) {
        this.expandedTestId = null;
        this.currentAssignments = [];
        return;
      }
      this.expandedTestId = t.id;
      this.currentAssignments = [];
      this.assignmentsLoading = true;
      try {
        this.currentAssignments = await this.adminService.getTestAssignments(t.id);
      } catch (e) {
        console.error('getTestAssignments', e);
      } finally {
        this.assignmentsLoading = false;
      }
    },
    async removeAssignment(testId: number, userId: number) {
      try {
        await this.adminService.removeTestAssignment(testId, userId);
        this.currentAssignments = this.currentAssignments.filter((a) => a.userId !== userId);
        const toast = await toastController.create({
          message: 'Назначение снято',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('removeAssignment', e);
        const toast = await toastController.create({
          message: 'Ошибка',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async resetAttempts(testId: number, userId: number) {
      try {
        await this.adminService.resetTestAttempts(testId, userId);
        const toast = await toastController.create({
          message: 'Попытки сброшены',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('resetAttempts', e);
        const toast = await toastController.create({
          message: 'Ошибка',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async openAttemptDetails(testId: number, userId: number) {
      this.showAttemptDetailsModal = true;
      this.attemptDetailsLoading = true;
      this.attemptDetails = null;
      try {
        const attempts = await this.adminService.getTestAttempts(testId, userId);
        const submitted = attempts.find((a) => !!a.submittedAt);
        if (!submitted) {
          const toast = await toastController.create({
            message: 'Завершённых попыток не найдено',
            duration: 2000,
            position: 'top',
            color: 'warning',
          });
          await toast.present();
          return;
        }
        this.attemptDetails = await this.adminService.getTestAttemptDetails(testId, submitted.id);
      } catch (e) {
        console.error('openAttemptDetails', e);
        const toast = await toastController.create({
          message: 'Не удалось загрузить детализацию',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.attemptDetailsLoading = false;
      }
    },
    closeAttemptDetailsModal() {
      this.showAttemptDetailsModal = false;
      this.attemptDetails = null;
      this.attemptDetailsLoading = false;
    },
    formatAttemptAnswers(answers: { answerText?: string }[]): string {
      if (!answers || answers.length === 0) {
        return 'нет ответа';
      }
      return answers.map((a) => a.answerText || '').filter(Boolean).join(', ') || 'нет ответа';
    },
    openAddUsersModal(t: IAdminTest) {
      this.addUsersTestId = t.id;
      this.showAddUsersModal = true;
      this.selectedUserIds = [];
      this.availableUsers = [];
      this.addUsersFilterQuery = '';
      this.addUsersRoleFilter = '';
      this.addUsersModalLoading = true;
      this.loadAvailableUsers();
    },
    async loadAvailableUsers() {
      if (this.addUsersTestId == null) return;
      const testId = this.addUsersTestId;
      try {
        const [assignments, raw] = await Promise.all([
          this.adminService.getTestAssignments(testId),
          this.adminService.getAllAdminUsers(),
        ]);
        if (this.addUsersTestId !== testId) return;
        const assignedIds = new Set(assignments.map((a) => a.userId));
        this.availableUsers = raw
          .filter((u) => u.id != null && !!u.login && !assignedIds.has(u.id))
          .map((u) => ({
            id: u.id as number,
            login: u.login as string,
            firstName: u.firstName,
            lastName: u.lastName,
            authorities: Array.isArray(u.authorities) ? [...u.authorities] : [],
          }));
      } catch (e) {
        console.error('fetch users', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки пользователей',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        this.closeAddUsersModal();
      } finally {
        this.addUsersModalLoading = false;
      }
    },
    closeAddUsersModal() {
      this.showAddUsersModal = false;
      this.addUsersTestId = null;
      this.availableUsers = [];
      this.selectedUserIds = [];
      this.addUsersFilterQuery = '';
      this.addUsersRoleFilter = '';
    },
    async openEditModal(t: IAdminTest) {
      this.editTestId = t.id;
      this.showEditModal = true;
      this.editLoading = true;
      this.editTest = {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        availableFromInput: '',
        roleNames: [] as string[],
      };
      try {
        const test = await this.adminService.getAdminTest(t.id);
        if (test) {
          const roleNamesRaw = test.roleNames ?? (test as { role_names?: string[] }).role_names ?? (test.rolesToGrant ?? []).map((r) => r.name);
          const roleNames = Array.isArray(roleNamesRaw) ? [...roleNamesRaw] : [];
          this.editTest = {
            name: test.name,
            description: test.description ?? '',
            passingThreshold: test.passingThreshold ?? 80,
            maxAttempts: test.maxAttempts ?? undefined,
            durationForm: this.durationMinutesToForm(test.durationMinutes),
            availableFromInput: test.availableFrom ? dayjs(test.availableFrom).format('YYYY-MM-DDTHH:mm') : '',
            roleNames: [],
          };
          this.$nextTick(() => {
            this.editTest.roleNames = roleNames;
          });
        } else {
          const toast = await toastController.create({
            message: 'Тест не найден',
            duration: 2000,
            position: 'top',
            color: 'warning',
          });
          await toast.present();
          this.closeEditModal();
        }
      } catch (e) {
        console.error('getAdminTest', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки теста',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        this.closeEditModal();
      } finally {
        this.editLoading = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
      this.openEditAvailableFrom = false;
      this.editTestId = null;
      this.editTest = {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        availableFromInput: '',
        roleNames: [] as string[],
      };
    },
    /** "ЧЧ:ММ" -> минуты или null если пусто/невалидно */
    onNewTestDurationInput(value: unknown): void {
      (this.newTest as TestFormVM).durationForm = value !== null && value !== undefined ? String(value) : '';
    },
    onEditTestDurationInput(value: unknown): void {
      (this.editTest as TestFormVM).durationForm = value !== null && value !== undefined ? String(value) : '';
    },
    onNewAvailableFromChange(e: CustomEvent) {
      const v = e.detail?.value;
      if (typeof v === 'string' && v) {
        this.newTest.availableFromInput = v.length >= 16 ? v.slice(0, 16) : v;
        this.openNewAvailableFrom = false;
      }
    },
    onEditAvailableFromChange(e: CustomEvent) {
      const v = e.detail?.value;
      if (typeof v === 'string' && v) {
        this.editTest.availableFromInput = v.length >= 16 ? v.slice(0, 16) : v;
        this.openEditAvailableFrom = false;
      }
    },
    clearNewAvailableFrom() {
      this.newTest.availableFromInput = '';
    },
    clearEditAvailableFrom() {
      this.editTest.availableFromInput = '';
    },
    availableFromInputToIso(input: string): string | null {
      const t = (input || '').trim();
      if (!t) return null;
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    },
    formatAvailableFromDisplay(iso: string | null | undefined): string {
      if (!iso) return '';
      return dayjs(iso).format('DD.MM.YYYY HH:mm');
    },
    formatAvailableFromLocal(localInput: string): string {
      if (!localInput) return '';
      return dayjs(localInput).format('DD.MM.YYYY HH:mm');
    },
    parseDurationForm(s: string): number | null {
      const t = (s || '').trim();
      if (!t) return null;
      const parts = t.split(':');
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      if (h < 0 || m < 0 || m > 59) return null;
      return h * 60 + m;
    },
    /** минуты -> "ЧЧ:ММ" */
    durationMinutesToForm(minutes: number | null | undefined): string {
      if (minutes == null || minutes === 0) return '';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    },
    formatDurationDisplay(minutes: number | null | undefined): string {
      if (minutes == null) return 'без ограничения';
      return this.durationMinutesToForm(minutes);
    },
    async saveEditTest() {
      if (this.editTestId == null || !this.editTest.name?.trim()) return;
      this.saving = true;
      try {
        await this.adminService.updateTest(this.editTestId, {
          name: this.editTest.name.trim(),
          description: this.editTest.description?.trim() || undefined,
          passingThreshold: this.editTest.passingThreshold ?? 80,
          maxAttempts: this.editTest.maxAttempts ?? undefined,
          durationMinutes: this.parseDurationForm(this.editTest.durationForm),
          availableFrom: this.availableFromInputToIso(this.editTest.availableFromInput),
          roleNames: this.editTest.roleNames ?? [],
        });
        const toast = await toastController.create({
          message: 'Тест сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeEditModal();
        await this.loadTests();
      } catch (e) {
        console.error('updateTest', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async openQuestionsModal(t: IAdminTest) {
      this.questionsTestId = t.id;
      this.questionsTestName = t.name;
      this.showQuestionsModal = true;
      this.questionsList = [];
      this.showAddQuestionForm = false;
      this.addAnswerForQid = null;
      this.editQuestionId = null;
      this.editAnswerId = null;
      this.questionsLoading = true;
      try {
        this.questionsList = await this.adminService.getTestQuestions(t.id);
      } catch (e) {
        console.error('getTestQuestions', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки вопросов',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.questionsLoading = false;
      }
    },
    closeQuestionsModal() {
      this.showQuestionsModal = false;
      this.questionsTestId = null;
      this.questionsTestName = '';
      this.questionsList = [];
      this.showAddQuestionForm = false;
      this.newQuestion = { text: '', multipleChoice: false, sortOrder: 0 };
      this.addAnswerForQid = null;
      this.newAnswer = { text: '', isCorrect: false };
      this.editQuestionId = null;
      this.editAnswerId = null;
    },
    cancelAddQuestion() {
      this.showAddQuestionForm = false;
      this.newQuestion = { text: '', multipleChoice: false, sortOrder: 0 };
    },
    async saveNewQuestion() {
      if (this.questionsTestId == null || !this.newQuestion.text?.trim()) return;
      this.saving = true;
      try {
        const created = await this.adminService.createQuestion(this.questionsTestId, {
          text: this.newQuestion.text.trim(),
          multipleChoice: this.newQuestion.multipleChoice,
          sortOrder: this.newQuestion.sortOrder ?? 0,
        });
        this.questionsList = [...this.questionsList, { ...created, answers: [] }];
        this.newQuestion = { text: '', multipleChoice: false, sortOrder: this.questionsList.length };
        const toast = await toastController.create({
          message: 'Вопрос добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.showAddQuestionForm = false;
      } catch (e) {
        console.error('createQuestion', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления вопроса',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    startEditQuestion(q: IAdminQuestion) {
      this.editQuestionId = q.id;
      this.editQuestionForm = {
        text: q.text ?? '',
        multipleChoice: !!q.multipleChoice,
        sortOrder: q.sortOrder ?? 0,
      };
    },
    async saveEditQuestion() {
      if (this.questionsTestId == null || this.editQuestionId == null) return;
      this.saving = true;
      try {
        await this.adminService.updateQuestion(this.questionsTestId, this.editQuestionId, {
          text: this.editQuestionForm.text.trim(),
          multipleChoice: this.editQuestionForm.multipleChoice,
          sortOrder: this.editQuestionForm.sortOrder ?? 0,
        });
        const idx = this.questionsList.findIndex((x) => x.id === this.editQuestionId);
        if (idx >= 0) {
          const q = this.questionsList[idx];
          this.questionsList = [
            ...this.questionsList.slice(0, idx),
            { ...q, text: this.editQuestionForm.text, multipleChoice: this.editQuestionForm.multipleChoice, sortOrder: this.editQuestionForm.sortOrder },
            ...this.questionsList.slice(idx + 1),
          ];
        }
        this.editQuestionId = null;
        const toast = await toastController.create({
          message: 'Вопрос сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('updateQuestion', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async deleteQuestionConfirm(q: IAdminQuestion) {
      if (this.questionsTestId == null) return;
      const alert = await alertController.create({
        header: 'Удалить вопрос?',
        message: q.text ? `«${q.text.substring(0, 50)}${q.text.length > 50 ? '…' : ''}»` : 'Вопрос будет удалён.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                const testId = this.questionsTestId;
                if (testId == null) return;
                await this.adminService.deleteQuestion(testId, q.id);
                this.questionsList = this.questionsList.filter((x) => x.id !== q.id);
                const toast = await toastController.create({
                  message: 'Вопрос удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteQuestion', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    startAddAnswer(q: IAdminQuestion) {
      this.addAnswerForQid = q.id;
      this.newAnswer = { text: '', isCorrect: false };
    },
    cancelAddAnswer() {
      this.addAnswerForQid = null;
      this.newAnswer = { text: '', isCorrect: false };
    },
    async saveNewAnswer(qid: number) {
      if (!this.newAnswer.text?.trim()) return;
      this.saving = true;
      try {
        const created = await this.adminService.createAnswer(qid, {
          text: this.newAnswer.text.trim(),
          isCorrect: this.newAnswer.isCorrect,
        });
        const qIdx = this.questionsList.findIndex((x) => x.id === qid);
        if (qIdx >= 0) {
          const q = this.questionsList[qIdx];
          const answers = [...(q.answers || []), created];
          this.questionsList = [...this.questionsList.slice(0, qIdx), { ...q, answers }, ...this.questionsList.slice(qIdx + 1)];
        }
        this.addAnswerForQid = null;
        this.newAnswer = { text: '', isCorrect: false };
        const toast = await toastController.create({
          message: 'Ответ добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('createAnswer', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления ответа',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    startEditAnswer(ans: IAdminAnswer) {
      this.editAnswerId = ans.id;
      this.editAnswerForm = { text: ans.text ?? '', isCorrect: !!ans.isCorrect };
    },
    async saveEditAnswer() {
      if (this.editAnswerId == null) return;
      this.saving = true;
      try {
        await this.adminService.updateAnswer(this.editAnswerId, {
          text: this.editAnswerForm.text.trim(),
          isCorrect: this.editAnswerForm.isCorrect,
        });
        this.questionsList = this.questionsList.map((q) => ({
          ...q,
          answers: (q.answers || []).map((a) =>
            a.id === this.editAnswerId
              ? { ...a, text: this.editAnswerForm.text, isCorrect: this.editAnswerForm.isCorrect }
              : a
          ),
        }));
        this.editAnswerId = null;
        const toast = await toastController.create({
          message: 'Ответ сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('updateAnswer', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async deleteAnswerConfirm(ans: IAdminAnswer) {
      const alert = await alertController.create({
        header: 'Удалить ответ?',
        message: ans.text ? `«${ans.text.substring(0, 50)}${ans.text.length > 50 ? '…' : ''}»` : 'Ответ будет удалён.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteAnswer(ans.id);
                this.questionsList = this.questionsList.map((q) => ({
                  ...q,
                  answers: (q.answers || []).filter((a) => a.id !== ans.id),
                }));
                const toast = await toastController.create({
                  message: 'Ответ удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteAnswer', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    toggleUserPick(userId: number) {
      const idx = this.selectedUserIds.indexOf(userId);
      if (idx >= 0) this.selectedUserIds.splice(idx, 1);
      else this.selectedUserIds.push(userId);
    },
    async confirmAddUsers() {
      if (this.addUsersTestId == null || !this.selectedUserIds.length) return;
      this.addingUsers = true;
      try {
        await this.adminService.addTestAssignments(this.addUsersTestId, [...this.selectedUserIds]);
        this.expandedTestId = this.addUsersTestId;
        this.currentAssignments = await this.adminService.getTestAssignments(this.addUsersTestId);
        const toast = await toastController.create({
          message: 'Пользователи добавлены',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeAddUsersModal();
      } catch (e) {
        console.error('addTestAssignments', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.addingUsers = false;
      }
    },
    async deleteTestConfirm(t: IAdminTest) {
      const alert = await alertController.create({
        header: 'Удалить тест?',
        message: `Тест «${t.name}» будет удалён вместе с вопросами и назначениями.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteTest(t.id);
                if (this.expandedTestId === t.id) this.expandedTestId = null;
                await this.loadTests();
                const toast = await toastController.create({
                  message: 'Тест удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteTest', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--ion-text-color);
}

.admin-card ion-card-content {
  color: var(--ion-text-color);
  opacity: 0.9;
}

.admin-card ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

.admin-card ion-item ion-label[position='stacked'] {
  margin-bottom: 6px;
}

.admin-available-from-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 6px 0 10px;
}

.admin-available-from-value {
  flex: 1 1 140px;
  color: var(--ion-text-color);
  opacity: 0.9;
  font-size: 0.95rem;
}

.admin-hint {
  color: var(--ion-text-color);
  opacity: 0.8;
  padding: 12px 0;
}

.admin-tests-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.admin-test-row {
  border: 1px solid var(--ion-border-color);
  border-radius: 8px;
  overflow: hidden;
}

.admin-test-main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .admin-test-main {
    flex-direction: column;
    align-items: stretch;
  }
  .admin-test-main ion-label {
    flex: none;
  }
  .admin-test-main ion-button {
    align-self: flex-start;
  }
  .admin-test-assignments {
    padding: 10px 12px;
  }
  .admin-assignments-list {
    margin: 0 -12px;
  }
  .admin-assignment-item {
    min-height: auto;
    --padding-start: 12px;
    --padding-end: 12px;
    --inner-padding-end: 12px;
    padding-top: 10px;
    padding-bottom: 10px;
    align-items: flex-start;
  }
  .admin-assignment-item ion-label {
    white-space: normal;
  }
  .admin-assignment-item .admin-assignment-actions {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid var(--ion-border-color);
  }
  .admin-assignment-status-row {
    flex-wrap: wrap;
    gap: 6px;
  }
  .admin-assignment-status,
  .admin-assignment-attempts,
  .admin-assignment-score {
    font-size: 0.7rem;
  }
  .admin-assignment-item ion-label h3 {
    font-size: 0.95rem;
    word-break: break-word;
  }
  .admin-assignment-login {
    font-size: 0.8rem;
  }
  .admin-assignments-search {
    margin: 0 -4px;
  }
}

.admin-test-main ion-label {
  flex: 1;
  min-width: 0;
}

.admin-test-main ion-label h2 {
  color: var(--ion-text-color);
}

.admin-test-main ion-label p {
  color: var(--ion-text-color);
  opacity: 0.8;
  font-size: 0.85rem;
}

.admin-test-assignments {
  padding: 12px 16px;
  border-top: 1px solid var(--ion-border-color);
  background: rgba(0, 0, 0, 0.2);
}

.admin-assignments-title {
  margin: 0 0 8px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.admin-assignments-list,
.admin-assignments-list ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
}
.admin-assignments-list {
  background: transparent !important;
}
.admin-assignments-list ion-item::part(native),
.admin-assignment-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}
.admin-assignment-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--ion-text-color);
}

.admin-assignment-login {
  margin: 0 0 6px;
  font-size: 0.85rem;
  opacity: 0.9;
}

.admin-assignments-search {
  --background: transparent;
  padding: 8px 0;
}

.admin-assignment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
}

.admin-assignment-item ion-label h3 {
  margin: 0 0 6px;
  font-weight: 600;
}

.admin-assignment-status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
}

.admin-assignment-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
}

.admin-assignment-status ion-icon {
  font-size: 0.95rem;
}

.admin-assignment-status--passed {
  background: var(--ion-color-success);
  color: var(--ion-color-success-contrast);
}

.admin-assignment-status--draft {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.admin-assignment-status--failed {
  background: var(--ion-color-danger);
  color: var(--ion-color-danger-contrast);
}

.admin-assignment-status--none {
  background: var(--ion-color-medium);
  color: var(--ion-color-medium-contrast);
}

.admin-assignment-attempts,
.admin-assignment-score {
  color: var(--ion-text-color);
  opacity: 0.9;
}

.admin-attempt-summary {
  margin-bottom: 12px;
  color: var(--ion-text-color);
}

.admin-attempt-summary p {
  margin: 4px 0;
}

.admin-attempt-question-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-attempt-question {
  border: 1px solid var(--ion-border-color);
  border-radius: 10px;
  padding: 10px;
}

.admin-attempt-question--correct {
  border-color: var(--ion-color-success);
}

.admin-attempt-question--wrong {
  border-color: var(--ion-color-danger);
}

.admin-attempt-question-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: var(--ion-text-color);
}

.admin-attempt-answer-line {
  margin: 8px 0 0;
  color: var(--ion-text-color);
  opacity: 0.9;
}

/* Кнопки в стиле приложения (оранжевые, как в других разделах) */
.admin-primary-btn,
.admin-primary-btn.ion-activated,
.admin-primary-btn.ion-focused {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
}

.admin-outline-btn,
.admin-outline-btn.ion-activated,
.admin-outline-btn.ion-focused {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  --color-hover: var(--ion-color-orange-tint);
}

.admin-card .admin-primary-btn,
.admin-card .admin-outline-btn {
  margin-top: 12px;
  margin-bottom: 4px;
}

.admin-card .admin-outline-btn {
  margin-top: 8px;
}

.admin-card-btn {
  --color: var(--ion-text-color);
  margin: 0 2px;
}

.admin-test-assignments .admin-card-btn {
  min-height: 32px;
}

.admin-modal-actions {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.admin-modal-actions .admin-primary-btn {
  margin-top: 0;
}

.admin-modal-actions .admin-outline-btn {
  margin-top: 0;
}

.admin-users-modal ion-list,
.admin-users-modal ion-item,
.admin-users-modal .admin-user-pick-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--liga-chrome-text);
}
.admin-users-modal ion-list {
  background: transparent !important;
}
.admin-users-modal ion-item::part(native),
.admin-users-modal .admin-user-pick-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.admin-users-modal .admin-user-pick-item ion-label h3,
.admin-users-modal .admin-user-pick-item ion-label p {
  color: var(--liga-chrome-text);
}

/* Чекбоксы в модалке «Добавить пользователей» */
.admin-users-modal ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: var(--ion-color-orange);
  --border-color: var(--liga-chrome-text-muted);
  --border-color-checked: var(--ion-color-orange);
  --checkmark-color: #ffffff;
  margin-right: 8px;
}
.admin-users-modal ion-checkbox::part(container) {
  border-radius: 6px;
  border-width: 2px;
}

.admin-users-modal-footer .admin-modal-actions {
  margin-top: 0;
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}
</style>

<!-- Стили без scoped для модалки: контент рендерится вне компонента, кнопки должны быть оранжевыми -->
<style>
.admin-users-modal {
  --border-radius: 18px 18px 0 0;
  --background: var(--liga-page-bg, var(--ion-background-color));
  --width: min(560px, 100%);
  --height: min(88vh, 720px);
}
.admin-users-modal::part(content) {
  border-radius: 18px 18px 0 0;
  border: 1px solid var(--liga-float-glass-border);
  box-shadow: var(--liga-float-glass-shadow);
  overflow: hidden;
}
.admin-users-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 14px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  background: var(--liga-page-bg, var(--ion-background-color));
  color: var(--liga-chrome-text);
  box-sizing: border-box;
}
.admin-users-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  flex: 0 0 auto;
}
.admin-users-head h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--liga-chrome-text);
}
.admin-users-close {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ion-color-orange);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.admin-users-status {
  margin: 18px 4px;
  color: var(--liga-chrome-text-muted);
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.35;
}
.admin-users-modal .admin-add-users-search {
  --background: var(--liga-float-glass-bg-strong);
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
  --icon-color: var(--liga-chrome-text-muted);
  --border-radius: 14px;
  flex: 0 0 auto;
  padding: 0;
  margin: 0 0 8px;
  border: 1px solid var(--liga-float-glass-border);
  border-radius: 14px;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
}
.admin-role-filter {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin: 0 0 10px;
  padding: 2px 2px 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  flex: 0 0 auto;
}
.admin-role-filter::-webkit-scrollbar {
  display: none;
}
.admin-role-chip {
  flex: 0 0 auto;
  margin: 0;
  padding: 7px 12px;
  border: 1px solid var(--liga-float-glass-border);
  border-radius: 999px;
  background: var(--liga-float-glass-bg-strong);
  color: var(--liga-chrome-text);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  cursor: pointer;
}
.admin-role-chip--active {
  border-color: rgba(235, 89, 82, 0.45);
  background: rgba(235, 89, 82, 0.14);
  color: var(--ion-color-orange);
}
.admin-role-chip:active {
  transform: scale(0.98);
}
.admin-users-list-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.admin-users-pick-list {
  background: transparent !important;
  padding: 0;
  margin: 0;
}
.admin-users-modal ion-list,
.admin-users-modal ion-item,
.admin-users-modal .admin-user-pick-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--liga-chrome-text);
}
.admin-users-modal .admin-user-pick-item {
  --min-height: 56px;
  --padding-start: 12px;
  --padding-end: 12px;
  --inner-padding-end: 0;
  margin-bottom: 6px;
  border-radius: 12px;
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg-strong) !important;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  overflow: hidden;
}
.admin-users-modal .admin-user-pick-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}
.admin-users-modal .admin-user-pick-item ion-label h3 {
  margin: 0;
  color: var(--liga-chrome-text);
  font-size: 0.95rem;
  font-weight: 600;
}
.admin-users-modal .admin-user-pick-item .admin-user-pick-login,
.admin-users-modal .admin-user-pick-item ion-label p {
  margin: 2px 0 0;
  color: var(--liga-chrome-text-muted);
  font-size: 0.8rem;
}
.admin-users-modal ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: var(--ion-color-orange) !important;
  --border-color: var(--liga-chrome-text-muted);
  --border-color-checked: var(--ion-color-orange) !important;
  --checkmark-color: #ffffff !important;
  margin-right: 10px;
}
.admin-users-modal ion-checkbox::part(container) {
  border-radius: 6px;
  border-width: 2px;
}
.admin-users-actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 4px;
}
.admin-users-modal .admin-primary-btn,
.admin-users-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --background-hover: var(--ion-color-orange-tint) !important;
  --color: #ffffff !important;
  --border-radius: 14px;
  font-weight: 600;
  margin: 0;
}
.admin-users-modal .admin-outline-btn,
.admin-users-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
  --border-radius: 14px;
  --background: transparent;
  font-weight: 600;
  margin: 0;
}

/* Модалка редактирования теста — оранжевые кнопки */
.admin-edit-test-modal .admin-primary-btn,
.admin-edit-test-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: var(--liga-chrome-text) !important;
}
.admin-edit-test-modal .admin-outline-btn,
.admin-edit-test-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-edit-test-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
.admin-edit-test-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-edit-test-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-edit-test-modal .admin-card ion-item,
.admin-edit-test-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

/* Модалка вопросов и ответов */
.admin-questions-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-question-block {
  border: 1px solid var(--ion-border-color);
  border-radius: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.15);
}

.admin-question-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-question-title {
  flex: 1;
  min-width: 0;
  color: var(--ion-text-color);
}

.admin-question-title strong {
  display: block;
  margin-bottom: 4px;
}

.admin-badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--ion-color-medium);
  color: var(--ion-color-light);
  margin-right: 8px;
}

.admin-badge.correct {
  background: var(--ion-color-success);
}

.admin-question-order {
  font-size: 0.8rem;
  opacity: 0.8;
  color: var(--ion-text-color);
}

.admin-question-actions {
  display: flex;
  gap: 4px;
}

.admin-answers-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
}

.admin-answer-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 0;
  border-bottom: 1px solid var(--ion-border-color);
  color: var(--ion-text-color);
}

.admin-answer-row:last-child {
  border-bottom: none;
}

.admin-answer-text {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.admin-answer-input {
  max-width: 200px;
  --padding-start: 8px;
  --padding-end: 8px;
}

/* Мобильная верстка модалки «Вопросы и ответы» */
@media (max-width: 768px) {
  .admin-questions-modal .admin-question-block {
    padding: 10px;
  }
  .admin-questions-modal .admin-question-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .admin-questions-modal .admin-question-title {
    flex: none;
  }
  .admin-questions-modal .admin-question-actions {
    flex-wrap: wrap;
  }
  .admin-questions-modal .admin-answer-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 10px 0;
  }
  .admin-questions-modal .admin-answer-text {
    flex: none;
    width: 100%;
    min-width: 0;
    line-height: 1.4;
    padding-right: 0;
  }
  .admin-questions-modal .admin-answer-row .admin-badge {
    align-self: flex-start;
  }
  .admin-questions-modal .admin-answer-row ion-button {
    align-self: flex-start;
  }
  .admin-questions-modal .admin-answer-input {
    max-width: none;
    width: 100%;
  }
  .admin-questions-modal .admin-add-answer .admin-answer-input,
  .admin-questions-modal .admin-add-answer ion-checkbox,
  .admin-questions-modal .admin-add-answer ion-button {
    width: 100%;
    max-width: none;
  }
  .admin-questions-modal .admin-edit-inline ion-input,
  .admin-questions-modal .admin-edit-inline ion-button {
    width: 100%;
    max-width: none;
  }
}

.admin-add-answer-btn {
  margin-top: 8px;
}

.admin-question-form.admin-card {
  margin: 0 0 16px;
  padding: 12px;
}

.admin-question-form ion-item,
.admin-edit-inline ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

.admin-edit-inline {
  margin-top: 8px;
  padding: 8px;
  border: 1px dashed var(--ion-border-color);
  border-radius: 8px;
}

.admin-add-answer {
  border: 1px dashed var(--ion-border-color);
  border-radius: 8px;
  padding: 8px;
  margin-top: 4px;
}
</style>

<!-- Модалка вопросов — оранжевые кнопки в стиле приложения (рендерится вне компонента) -->
<style>
.admin-questions-modal .admin-primary-btn,
.admin-questions-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: var(--liga-chrome-text) !important;
}
.admin-questions-modal .admin-outline-btn,
.admin-questions-modal .admin-outline-btn.ion-activated,
.admin-questions-modal .admin-add-question-btn,
.admin-questions-modal .admin-add-question-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
/* Чекбокс и кнопки в строке редактирования ответа */
.admin-questions-modal .admin-answer-row ion-checkbox.admin-answer-checkbox,
.admin-questions-modal .admin-answer-row ion-checkbox {
  --size: 22px;
  --checkbox-background-checked: var(--ion-color-orange) !important;
  --checkmark-color: var(--liga-chrome-text) !important;
  --border-color: var(--ion-color-medium);
  --border-color-checked: var(--ion-color-orange) !important;
  margin-right: 8px;
}
.admin-questions-modal .admin-answer-row ion-checkbox.admin-answer-checkbox::part(container),
.admin-questions-modal .admin-answer-row ion-checkbox::part(container) {
  border-radius: 4px;
}
.admin-questions-modal .admin-answer-row .admin-primary-btn {
  margin-right: 4px;
}
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-questions-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}
.admin-questions-content {
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}
.admin-questions-modal .admin-answer-input,
.admin-questions-modal .admin-add-answer .admin-answer-input {
  margin-left: 12px;
  margin-right: 12px;
  width: calc(100% - 24px);
  max-width: calc(100% - 24px);
}
.admin-questions-modal .admin-add-answer,
.admin-questions-modal .admin-edit-inline {
  padding-left: 12px;
  padding-right: 12px;
}
</style>

<style>
/* Модалка вопросов — оранжевые кнопки (контент рендерится вне компонента) */
.admin-questions-modal .admin-primary-btn,
.admin-questions-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: var(--liga-chrome-text) !important;
}
.admin-questions-modal .admin-outline-btn,
.admin-questions-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-questions-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}
</style>
